import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { requestSellerInvoicesZip } from "@/lib/api/invoice";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import ButtonsHeader from "@/app/components/common/ButtonsHeader";

const formatDateInputValue = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default function SellerInvoicesPage() {
  const { data: session } = useAuth();
  const [startDate, setStartDate] = React.useState(() => {
    const date = new Date();
    date.setDate(1);
    return formatDateInputValue(date);
  });
  const [endDate, setEndDate] = React.useState(() =>
    formatDateInputValue(new Date()),
  );
  const [requesting, setRequesting] = React.useState(false);

  const sellerId = session?.user?.sellers?.[0]?.id;

  const handleRequest = async () => {
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates");
      return;
    }

    if (startDate > endDate) {
      toast.error("Start date must be on or before end date");
      return;
    }

    setRequesting(true);

    try {
      const result = await requestSellerInvoicesZip(
        startDate,
        endDate,
        sellerId,
      );

      if (!result) {
        toast.error("No invoices found for the selected date range");
        return;
      }

      toast.success(result.message);
    } catch {
      toast.error("Failed to request invoices");
    } finally {
      setRequesting(false);
    }
  };

  return (
    <div className="mx-auto w-full min-h-screen pb-24 py-6">
      <ButtonsHeader heading="Request Invoices" />

      <div className="mx-5 mt-8 rounded-3xl bg-white p-6 shadow-sm">
        <p className="text-sm text-muted-foreground">
          Select a date range to receive all your invoices as a ZIP file by
          email.
        </p>

        <div className="mt-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="startDate">Start Date</Label>
            <Input
              id="startDate"
              type="date"
              value={startDate}
              max={endDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="endDate">End Date</Label>
            <Input
              id="endDate"
              type="date"
              value={endDate}
              min={startDate}
              max={formatDateInputValue(new Date())}
              onChange={(event) => setEndDate(event.target.value)}
            />
          </div>
        </div>

        <Button
          type="button"
          className="mt-6 w-full rounded-full"
          disabled={requesting}
          onClick={() => void handleRequest()}
        >
          {requesting ? "Submitting request..." : "Request Invoices ZIP"}
        </Button>
      </div>
    </div>
  );
}
